import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, MapPin, PhoneCall, ChevronDown, Share2, HeartHandshake, Navigation, Home, Activity } from "lucide-react";
import { Helmet } from "react-helmet-async";
import toast from "react-hot-toast";

export default function Charity() {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // 🔥 Charity & Care Homes Data 🔥
  const charityData = [
    {
      id: 1,
      name: "മിനാർ - ദി പീപ്പിൾസ് വില്ലേജ്",
      englishName: "Minar - The People's Village",
      category: "Care Home",
      place: "Mottambrum, Madayi, Kerala 670304",
      phone: "04972877900",
      description: "മാടായി മൊട്ടമ്പ്രം പ്രദേശത്ത് പ്രവർത്തിക്കുന്ന സാന്ത്വന കേന്ദ്രം. അനാഥർക്കും അഗതികൾക്കും ഒരു സ്നേഹത്തണലായി ഈ സ്ഥാപനം പ്രവർത്തിക്കുന്നു.",
      icon: Home,
      color: "from-rose-400 to-red-500",
      mapQuery: "Minar+The+Peoples+Village+Mottambrum+Madayi"
    },
    {
      id: 2,
      name: "ഹോപ്പ് റീഹാബിലിറ്റേഷൻ സെന്റർ",
      englishName: "HOPE Rehabilitation Center",
      category: "Rehabilitation",
      place: "Kadannapally Rd, Pilathara, Kerala 670501",
      phone: "04972802803",
      description: "പിലാത്തറ കടന്നപ്പള്ളി റോഡിൽ പ്രവർത്തിക്കുന്ന പുനരധിവാസ കേന്ദ്രം. ശാരീരിക-മാനസിക വെല്ലുവിളികൾ നേരിടുന്നവർക്ക് വിദഗ്ദ്ധ പരിചരണം നൽകുന്നു.",
      icon: Activity,
      color: "from-teal-400 to-emerald-500",
      mapQuery: "HOPE+Rehabilitation+Center+Pilathara"
    },
    {
      id: 3,
      name: "ഗാർഡിയൻ ഏഞ്ചൽസ് കെയർ ഹോം",
      englishName: "Guardian Angels Care Home",
      category: "Care Home",
      place: "Podithadam, Ezhome, Kerala 670334",
      phone: "04842845600", 
      description: "ഏഴോം പൊടിത്തടം ഭാഗത്ത് പ്രവർത്തിക്കുന്ന കെയർ ഹോം. വയോധികർക്കും ആശ്രയം ആവശ്യമുള്ളവർക്കും സ്നേഹത്തണലും പരിചരണവും നൽകുന്നു.",
      icon: HeartHandshake,
      color: "from-blue-400 to-indigo-500",
      mapQuery: "Guardian+Angels+care+home+Podithadam+Ezhome"
    }
  ];

  // Filter Logic
  const filteredCharities = charityData.filter((c) => {
    return (
      (!categoryFilter || c.category === categoryFilter) &&
      (c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.englishName.toLowerCase().includes(search.toLowerCase()) ||
        c.place.toLowerCase().includes(search.toLowerCase()))
    );
  });

  // Share Function
  const handleShareAll = async () => {
    const shareText = "INFO PAYANGADI: നമ്മുടെ നാട്ടിലെ സാന്ത്വന കേന്ദ്രങ്ങളെയും കെയർ ഹോമുകളെയും കുറിച്ചറിയാൻ ഈ ലിങ്ക് സന്ദർശിക്കുക.";
    const shareUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({ title: "Charity & Care Homes", text: shareText, url: shareUrl });
      } catch (error) {
        console.log("ഷെയർ ചെയ്യാൻ കഴിഞ്ഞില്ല:", error);
      }
    } else {
      navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success("ലിങ്ക് കോപ്പി ചെയ്തു! ഇനി ഷെയർ ചെയ്യാം.");
    }
  };

  return (
    <>
      <Helmet>
        <title>Charity & Care Homes in Kalliasseri | Info Payangadi</title>
        <meta name="description" content="പഴയങ്ങാടി, കല്യാശ്ശേരി ഭാഗങ്ങളിലെ അനാഥാലയങ്ങൾ, സാന്ത്വന കേന്ദ്രങ്ങൾ, കെയർ ഹോമുകൾ എന്നിവയുടെ വിവരങ്ങൾ." />
        <meta name="keywords" content="Charity Payangadi, Orphanage Kalliasseri, Care homes Kannur, Minar Peoples Village, Hope Rehabilitation" />
        <meta property="og:title" content="Charity & Care Homes - Info Payangadi" />
        <meta property="og:image" content={`${window.location.origin}/og-image.jpg`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 p-4 sm:p-6 pb-28 selection:bg-rose-200">
        
        {/* 🔥 HERO SECTION 🔥 */}
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between mb-8 mt-2 gap-4">
          
          <div className="flex items-center gap-3">
            <div className="p-3 sm:p-4 bg-rose-100 text-rose-600 rounded-full shadow-inner">
              <HeartHandshake className="w-8 h-8 sm:w-9 sm:h-9" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                Charity & <span className="text-rose-600">Care Homes</span>
              </h2>
              <p className="text-gray-500 text-xs sm:text-sm mt-0.5">സ്നേഹത്തിന്റെയും കാരുണ്യത്തിന്റെയും തണലിടങ്ങൾ</p>
            </div>
          </div>
          
          <div className="flex w-full md:w-auto mt-2 md:mt-0">
            <button
              onClick={handleShareAll}
              className="flex-1 md:flex-none flex items-center justify-center gap-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-5 py-2.5 rounded-xl shadow-sm transition-all active:scale-95 font-bold text-sm"
            >
              <Share2 size={16} />
              <span>Share Page</span>
            </button>
          </div>

        </div>

        {/* 🔍 SEARCH & FILTER SECTION 🔥 */}
        <div className="max-w-4xl mx-auto mb-10 flex flex-col sm:flex-row gap-3">
          
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="സ്ഥാപനത്തിന്റെ പേരോ സ്ഥലമോ തിരയുക..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-rose-400 outline-none transition-all text-sm sm:text-base font-medium"
            />
          </div>

          <div className="relative w-full sm:w-56 flex-shrink-0">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full appearance-none py-3 pl-4 pr-10 rounded-2xl border border-gray-200 shadow-sm bg-white focus:ring-2 focus:ring-rose-400 outline-none transition-all text-gray-700 font-bold text-sm sm:text-base"
            >
              <option value="">എല്ലാ സ്ഥാപനങ്ങളും</option>
              <option value="Care Home">Care Homes / Orphanages</option>
              <option value="Rehabilitation">Rehabilitation Centers</option>
            </select>
            <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          </div>
        </div>

        {/* 🤝 CHARITY HOMES GRID 🔥 */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 max-w-6xl mx-auto">
          <AnimatePresence>
            {filteredCharities.map((item) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-[2rem] shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col overflow-hidden group"
                >
                  {/* Card Header Illustration (Gradient + Icon) */}
                  <div className={`h-28 sm:h-32 bg-gradient-to-r ${item.color} relative flex items-center justify-center overflow-hidden`}>
                    <div className="absolute inset-0 bg-black/10"></div>
                    <Icon size={56} strokeWidth={1.5} className="text-white/80 drop-shadow-md group-hover:scale-110 transition-transform duration-500" />
                    
                    <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full border border-white/30 shadow-sm">
                      <span className="text-[10px] sm:text-xs font-bold text-white uppercase tracking-wider">
                        {item.category}
                      </span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 sm:p-6 flex flex-col flex-grow">
                    <div className="mb-3">
                      <h3 className="text-xl sm:text-2xl font-black text-gray-900 leading-tight">
                        {item.name}
                      </h3>
                      <p className="text-xs sm:text-sm font-bold text-rose-600 uppercase tracking-widest mt-1">
                        {item.englishName}
                      </p>
                    </div>
                    
                    <p className="text-sm text-gray-600 flex items-start gap-1.5 mb-3 font-medium">
                      <MapPin size={16} className="text-rose-400 mt-0.5 flex-shrink-0" /> 
                      {item.place}
                    </p>

                    <p className="text-sm text-gray-500 leading-relaxed mb-6 flex-grow border-t border-gray-50 pt-3">
                      {item.description}
                    </p>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2 mt-auto">
                      {item.phone ? (
                        <a 
                          href={`tel:${item.phone}`}
                          className="flex-1 flex items-center justify-center gap-2 bg-green-50 hover:bg-green-600 text-green-700 hover:text-white py-3 rounded-xl transition-colors font-bold text-sm shadow-sm"
                        >
                          <PhoneCall size={16} />
                          Call Now
                        </a>
                      ) : (
                        <button 
                          disabled
                          className="flex-1 flex items-center justify-center gap-2 bg-gray-50 text-gray-400 py-3 rounded-xl font-bold text-sm cursor-not-allowed"
                        >
                          <PhoneCall size={16} />
                          No Number
                        </button>
                      )}
                      
                      <a 
                        href={`https://www.google.com/maps/search/?api=1&query=${item.mapQuery}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white py-3 rounded-xl transition-colors font-bold text-sm shadow-sm"
                      >
                        <Navigation size={16} />
                        Direction
                      </a>
                    </div>

                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {/* No Results Found */}
          {filteredCharities.length === 0 && (
            <div className="col-span-full text-center bg-white p-10 rounded-3xl border border-gray-100">
              <HeartHandshake size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-bold text-gray-700">സ്ഥാപനം കണ്ടെത്താനായില്ല</h3>
              <p className="text-sm text-gray-500 mt-1">നിങ്ങൾ തിരഞ്ഞ വിവരങ്ങൾ ലഭ്യപ്പല്ല.</p>
            </div>
          )}
        </div>

      </div>
    </>
  );
}